import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, Sparkles, Linkedin, Twitter, Youtube, Instagram, Mail, Users, BookOpen } from "lucide-react";
import { SiteLayout } from "@/layouts/site-layout";
import { supabase } from "@/integrations/supabase/client";
import { IntegrationShowcaseSection } from "@/components/IntegrationShowcaseSection";
import { PricingSection } from "@/components/PricingSection";
import { toast } from "sonner";
import { OfferMarquee } from "@/components/OfferMarquee";
import { OfferStickyButton } from "@/components/OfferStickyButton";
import { HeroSection } from "@/components/ui/hero-section";

type OfferData = {
  id: string;
  partner_name: string;
  partner_email: string;
  partner_linkedin: string | null;
  partner_x_handle: string | null;
  partner_youtube: string | null;
  partner_instagram: string | null;
  community_link: string | null;
  newsletter_link: string | null;
  coupon_code: string;
  discount_percentage: number;
  idea_submission: string;
  created_at: string;
};

const Offer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const loadOffer = async () => {
      if (!id) {
        toast.error("Invalid offer link");
        navigate("/");
        return;
      }

      // Fetch the offer from database using the UUID
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('offer_uuid', id)
        .in('status', ['offer_sent', 'accepted'])
        .maybeSingle();

      if (error) {
        toast.error("Failed to load offer");
        navigate("/");
        return;
      }

      if (!data) {
        toast.error("Offer not found or no longer available");
        navigate("/");
        return;
      }

      setOfferData(data as OfferData);
      
      // Calculate time left (48 hours from creation)
      const createdAt = new Date(data.created_at).getTime();
      const expiresAt = createdAt + (48 * 60 * 60 * 1000); // 48 hours
      const now = Date.now();
      const diff = Math.max(0, expiresAt - now);
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ hours, minutes, seconds });
      setLoading(false);

      // Save offer details to cookies using database values
      document.cookie = `offer_partner=${data.partner_name}; path=/; max-age=604800`;
      document.cookie = `offer_coupon=${data.coupon_code}; path=/; max-age=604800`;
    };

    loadOffer();
  }, [id, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!offerData) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [offerData]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading your exclusive offer...</div>
        </div>
      </SiteLayout>
    );
  }

  if (!offerData) {
    return null;
  }

  const regularPrice = 1999;
  const discountedBasePrice = 999;
  const partnerDiscountedPrice = discountedBasePrice * (1 - (offerData.discount_percentage / 100));

  const handleClaimOffer = () => {
    toast.success("Coupon code copied! Redirecting to booking...");
    navigator.clipboard.writeText(offerData.coupon_code);
    setTimeout(() => {
      window.open("https://cal.com/buildwithrishi/15min", "_blank");
    }, 1000);
  };

  return (
    <SiteLayout>
      {/* Sticky CTA Button */}
      <OfferStickyButton timeLeft={timeLeft} onClick={handleClaimOffer} />

      {/* Hero Section */}
      <HeroSection />

      <main className="pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Partner Announcement Banner */}
          <div className="mb-12 text-center">
            <Card className="p-8 bg-gradient-to-r from-primary/5 via-background to-primary/5 border-primary/20">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-heading text-2xl shadow-lg">
                    {offerData.partner_name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="font-heading text-2xl text-foreground">{offerData.partner_name}</p>
                    <p className="text-sm text-muted-foreground">Official OneWeekMVPs Partner</p>
                  </div>
                </div>
                <p className="text-xl font-medium text-foreground max-w-3xl">
                  {offerData.partner_name} is offering you an additional <span className="text-primary font-bold">{offerData.discount_percentage}% discount</span> on MVP Development Services with OneWeekMVPs
                </p>
                <p className="text-base text-muted-foreground max-w-3xl">
                  As a trusted partner, I've personally worked with OneWeekMVPs and can vouch for their exceptional quality. Get your production-ready MVP delivered in just 7 days at an exclusive price!
                </p>
              </div>
            </Card>
          </div>

          {/* Offer Marquee - After Partner Banner */}
          <div className="mb-12">
            <OfferMarquee />
          </div>
          {/* Partner Badge */}
          <div className="text-center mb-8">
            <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground px-6 py-2 text-base">
              <Sparkles className="w-4 h-4 mr-2" />
              OneWeekMVPs Official Partner
            </Badge>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-heading text-5xl md:text-6xl mb-4 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                Congratulations, {offerData.partner_name}!
              </h1>
              <p className="text-2xl text-muted-foreground mb-4">
                Your exclusive <span className="text-foreground font-heading">{offerData.discount_percentage}% Partner Discount</span> is ready
              </p>
              <p className="text-lg text-muted-foreground">
                As an official OneWeekMVPs partner, you get priority access to our flagship MVP build service
              </p>
            </div>

            {/* Countdown Timer */}
            <Card className="p-6 mb-12 border-accent/40 bg-gradient-to-r from-accent/5 to-primary/5">
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Clock className="w-6 h-6 text-accent" />
                <p className="text-lg text-muted-foreground">Your exclusive partner offer expires in:</p>
                <div className="flex gap-2">
                  <div className="bg-card border-2 border-accent/40 rounded-lg px-4 py-2 shadow-lg">
                    <span className="text-3xl font-heading text-accent">{timeLeft.hours.toString().padStart(2, '0')}</span>
                    <span className="text-xs text-muted-foreground block">hours</span>
                  </div>
                  <span className="text-3xl font-heading text-accent">:</span>
                  <div className="bg-card border-2 border-accent/40 rounded-lg px-4 py-2 shadow-lg">
                    <span className="text-3xl font-heading text-accent">{timeLeft.minutes.toString().padStart(2, '0')}</span>
                    <span className="text-xs text-muted-foreground block">minutes</span>
                  </div>
                  <span className="text-3xl font-heading text-accent">:</span>
                  <div className="bg-card border-2 border-accent/40 rounded-lg px-4 py-2 shadow-lg">
                    <span className="text-3xl font-heading text-accent">{timeLeft.seconds.toString().padStart(2, '0')}</span>
                    <span className="text-xs text-muted-foreground block">seconds</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Your Idea */}
            <Card className="p-8 mb-12 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <h2 className="font-heading text-3xl mb-4 text-center">Your MVP Idea</h2>
              <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
                {offerData.idea_submission}
              </p>
            </Card>

            {/* The Partner Offer */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Left Column */}
              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-3xl mb-4">Your Exclusive Partner Offer</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    As an official partner, you get <span className="text-accent font-semibold">{offerData.discount_percentage}% OFF</span> our flagship 1-Week MVP package — delivered in <span className="text-foreground font-semibold">7 Days</span> with a 100% satisfaction guarantee.
                  </p>

                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Regular Price</p>
                    <div className="text-3xl font-heading text-muted-foreground line-through mb-1">
                      ${regularPrice.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">Discounted Price</p>
                    <div className="text-2xl font-heading text-muted-foreground line-through mb-3">
                      ${discountedBasePrice.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Your Exclusive Partner Price</p>
                    <div className="text-5xl font-heading bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent mb-2">
                      ${Math.round(partnerDiscountedPrice).toLocaleString()}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="border-accent/40 text-accent">
                        {offerData.discount_percentage}% Partner Discount
                      </Badge>
                      <Badge variant="outline" className="border-primary/40 text-primary">
                        7-Day Delivery
                      </Badge>
                    </div>
                  </div>

                  <Card className="p-4 border-accent/40 bg-accent/10">
                    <p className="text-sm font-medium mb-2">Your Partner Coupon Code:</p>
                    <code className="text-xl font-mono text-accent">{offerData.coupon_code}</code>
                  </Card>
                </div>

                {/* Partner Info */}
                <div>
                  <h3 className="font-heading text-xl mb-4">Your Partner Account Manager</h3>
                  <Card className="p-4 border-primary/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-primary-foreground font-heading text-xl">
                        {offerData.partner_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{offerData.partner_name}</p>
                        <p className="text-sm text-muted-foreground">Official Partner</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {offerData.partner_email && (
                        <a href={`mailto:${offerData.partner_email}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Mail className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.partner_linkedin && (
                        <a href={offerData.partner_linkedin} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Linkedin className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.partner_x_handle && (
                        <a href={`https://x.com/${offerData.partner_x_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Twitter className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.partner_youtube && (
                        <a href={offerData.partner_youtube} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Youtube className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.partner_instagram && (
                        <a href={`https://instagram.com/${offerData.partner_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Instagram className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.community_link && (
                        <a href={offerData.community_link} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <Users className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {offerData.newsletter_link && (
                        <a href={offerData.newsletter_link} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline">
                            <BookOpen className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </Card>
                </div>
              </div>

              {/* Right Column - Features */}
              <div>
                <h3 className="font-heading text-2xl mb-4">Everything Included</h3>
                <Card className="p-6 border-accent/20">
                  <ul className="space-y-3">
                    {[
                      "Complete UI/UX design with your branding",
                      "User authentication & account management",
                      "Admin dashboard for full control",
                      "Payment processing integration (Stripe)",
                      "AI integrations (OpenAI, Claude, etc.)",
                      "Responsive design (mobile + desktop)",
                      "Production deployment on Vercel",
                      "Database setup (Supabase)",
                      "Email notifications & workflows",
                      "Analytics & tracking setup",
                      "SEO optimization",
                      "30 days post-launch support",
                      "Priority partner support line",
                      "Source code ownership"
                    ].map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>

            {/* CTA Section */}
            <Card className="p-8 mb-12 border-accent/40 bg-gradient-to-r from-accent/10 to-primary/10">
              <div className="text-center space-y-6">
                <h2 className="font-heading text-3xl">Ready to Build Your MVP?</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Use your partner coupon code <code className="bg-background/80 px-2 py-1 rounded text-accent font-mono">{offerData.coupon_code}</code> at checkout to claim your {offerData.discount_percentage}% discount
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="text-lg px-8"
                    onClick={handleClaimOffer}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Claim Partner Offer
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="text-lg px-8"
                    onClick={() => navigate("/packages")}
                  >
                    View Package Details
                  </Button>
                </div>
              </div>
            </Card>

            {/* Partner Benefits */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="p-6 border-primary/20 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Priority Access</h3>
                <p className="text-sm text-muted-foreground">
                  Jump the queue with priority scheduling for your MVP build
                </p>
              </Card>
              <Card className="p-6 border-primary/20 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Guaranteed Quality</h3>
                <p className="text-sm text-muted-foreground">
                  100% satisfaction guarantee or your money back, no questions asked
                </p>
              </Card>
              <Card className="p-6 border-primary/20 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Direct Support</h3>
                <p className="text-sm text-muted-foreground">
                  Direct line to your dedicated partner account manager
                </p>
              </Card>
            </div>

            {/* Footer Note */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground italic">
                This exclusive partner offer expires in 48 hours. Your MVP will be delivered within 7 days or you get a full refund.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Additional Sections */}
      <IntegrationShowcaseSection />
      <PricingSection />
    </SiteLayout>
  );
};

export default Offer;
